import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

const CashFlowChart = ({ transactions = [] }) => {
  const chartData = useMemo(() => {
    if (!transactions || transactions.length === 0) {
      // Return empty state when no transactions
      return null;
    }

    // Process transaction data directly in useMemo
    // Group transactions by date period
    const groupedData = {};
    let minDate = new Date(8640000000000000); // Max date
    let maxDate = new Date(-8640000000000000); // Min date
    
    transactions.forEach(transaction => {
      const date = new Date(transaction.txn_date);
      if (date < minDate) minDate = date;
      if (date > maxDate) maxDate = date;
    });

    // Determine if we should group by month or by date
    const monthsDiff = (maxDate.getFullYear() - minDate.getFullYear()) * 12 + 
                      (maxDate.getMonth() - minDate.getMonth());
    
    const groupByMonth = monthsDiff > 0; // If more than current month, group by month
    
    transactions.forEach(transaction => {
      const date = new Date(transaction.txn_date);
      let key;
      
      if (groupByMonth) {
        // Group by month
        key = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      } else {
        // Group by date
        key = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
      
      if (!groupedData[key]) {
        groupedData[key] = { income: 0, expense: 0 };
      }
      
      const credit = transaction.credit || 0;
      const debit = transaction.debit || 0;
      
      groupedData[key].income += credit;
      groupedData[key].expense += debit;
    });

    // Sort keys chronologically
    const sortedKeys = Object.keys(groupedData).sort((a, b) => {
      if (groupByMonth) {
        return new Date(a + ' 1') - new Date(b + ' 1');
      } else {
        // For dates within same month, we need current year
        const currentYear = new Date().getFullYear();
        return new Date(a + ' ' + currentYear) - new Date(b + ' ' + currentYear);
      }
    });

    // Extract data for chart
    const categories = sortedKeys;
    const incomeData = sortedKeys.map(key => groupedData[key].income);
    const expenseData = sortedKeys.map(key => groupedData[key].expense);
    
    // Calculate totals
    const totalInflow = incomeData.reduce((sum, val) => sum + val, 0);
    const totalOutflow = expenseData.reduce((sum, val) => sum + val, 0);
    const netFlow = totalInflow - totalOutflow;
    
    const title = groupByMonth ? 
      'Monthly Cash Flow (From Uploaded Data)' : 
      'Daily Cash Flow (From Uploaded Data)';

    return {
      categories,
      incomeData,
      expenseData,
      totalInflow,
      totalOutflow,
      netFlow,
      title
    };
  }, [transactions]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // If no data, show empty state
  if (!chartData) {
    return (
      <section className="mb-8">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-800">Cash Flow Analysis</h3>
            <div className="flex items-center text-xs text-gray-400">
              <i className="fa-solid fa-chart-line mr-1"></i>
              <span>Waiting for data</span>
            </div>
          </div>
          <div className="px-6 py-16 text-center text-gray-500">
            <div className="max-w-sm mx-auto">
              <div className="mb-4">
                <i className="fa-solid fa-chart-bar text-6xl text-gray-300"></i>
              </div>
              <h4 className="text-lg font-medium text-gray-700 mb-2">
                No Cash Flow Data
              </h4>
              <p className="text-sm text-gray-500 mb-4">
                Upload a PDF bank statement to generate cash flow analysis and charts.
              </p>
              <div className="inline-flex items-center text-xs text-gray-400">
                <i className="fa-solid fa-robot mr-2"></i>
                <span>AI-powered analysis ready</span>
              </div>
            </div>
          </div>
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-400">—</div>
                <div className="text-sm text-gray-500">Total Inflow</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-400">—</div>
                <div className="text-sm text-gray-500">Total Outflow</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-400">—</div>
                <div className="text-sm text-gray-500">Net Flow</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Chart options for when we have data
  const chartOptions = {
    chart: {
      type: 'column',
      style: {
        fontFamily: 'Inter, sans-serif'
      }
    },
    title: {
      text: null
    },
    xAxis: {
      categories: chartData.categories,
      crosshair: true
    },
    yAxis: {
      min: 0,
      title: {
        text: 'Amount ($)'
      }
    },
    tooltip: {
      headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
      pointFormat: '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
        '<td style="padding:0"><b>${point.y:,.2f}</b></td></tr>',
      footerFormat: '</table>',
      shared: true,
      useHTML: true
    },
    plotOptions: {
      column: {
        pointPadding: 0.2,
        borderWidth: 0
      }
    },
    colors: ['#22c55e', '#ef4444'],
    series: [{
      name: 'Income',
      data: chartData.incomeData
    }, {
      name: 'Expenses',
      data: chartData.expenseData
    }],
    credits: {
      enabled: false
    }
  };

  return (
    <section className="mb-8">
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-800">{chartData.title}</h3>
          <div className="flex items-center text-xs text-green-600">
            <i className="fa-solid fa-check-circle mr-1"></i>
            <span>Data from uploaded PDF</span>
          </div>
        </div>
        <div className="h-80 p-4">
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
          />
        </div>
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(chartData.totalInflow)}
              </div>
              <div className="text-sm text-green-700">Total Inflow</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">
                {formatCurrency(chartData.totalOutflow)}
              </div>
              <div className="text-sm text-red-700">Total Outflow</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {formatCurrency(chartData.netFlow)}
              </div>
              <div className="text-sm text-blue-700">Net Flow</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CashFlowChart; 